/**
 * VesselMarkerPreview — design-review surface for CC-ICON-001 (v2).
 *
 * Presents all ten C-logo-derived concepts, each rendered at
 * 24 / 32 / 40 / 56px on both light and dark map surfaces, with a
 * design rationale for the PO. Deep-link: `#/design/vessel-marker`.
 */
import React, { useState, useMemo } from "react";
import VesselMarker, { VESSEL_MARKER_VARIANTS, CANONICAL_VARIANT } from "@/components/design-system/VesselMarker";

const SIZES = [24, 32, 40, 56];
const HEADINGS = [0, 45, 90, 180, 270];

export default function VesselMarkerPreview() {
  const [heading, setHeading] = useState(0);
  const [pulse, setPulse] = useState(false);

  // Approved variant floats to the top; explorations follow in a
  // separate "reference" section so the review page tells the story
  // "here is the ratified marker, and here is how we arrived at it."
  const approved = useMemo(
    () => VESSEL_MARKER_VARIANTS.find((v) => v.id === CANONICAL_VARIANT),
    []
  );
  const explorations = useMemo(
    () => VESSEL_MARKER_VARIANTS.filter((v) => v.id !== CANONICAL_VARIANT),
    []
  );

  return (
    <div className="min-h-screen bg-[#F6F7F9]">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-8 py-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-[10.5px] font-black tracking-[0.18em] uppercase text-[#C28E42]">
            Design System · CC-ICON-001 (Ratified · 2026-08-04)
          </div>
          <h1 className="font-display font-bold text-[28px] leading-tight text-[#0F172A] mt-1">
            Cruise Command Vessel Marker
          </h1>
          <p className="text-[14px] text-[#475569] mt-2 max-w-2xl leading-relaxed">
            The Cruise Command <span className="font-semibold text-[#0F2244]">"C"</span>
            {" "}as a navigation marker — the ship becomes <em>my ship</em>. The canonical
            variant is <strong className="text-[#0F172A]">Concept H · Heading C</strong>,
            ratified per the PO brief on 2026-08-04. The nine exploration variants remain
            below for reference and will retire after one release cycle.
          </p>
          <div className="mt-3 text-[11.5px] text-[#94A3B8] italic">
            The earlier ship-silhouette exploration is archived at
            <code className="mx-1 px-1 py-0.5 rounded bg-slate-100 text-[#475569] font-mono text-[10.5px]">
              /assets/vessel-markers/_archive_ship_silhouette_2026-08-04/
            </code>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="max-w-6xl mx-auto px-8 pt-6">
        <div className="flex flex-wrap items-center gap-4 bg-white rounded-xl border border-slate-200 px-5 py-4">
          <div className="flex items-center gap-3">
            <span className="text-[11px] font-black tracking-[0.15em] uppercase text-[#64748B]">
              Heading (future)
            </span>
            <div className="flex gap-1.5">
              {HEADINGS.map((h) => (
                <button
                  key={h}
                  type="button"
                  onClick={() => setHeading(h)}
                  className={`px-2.5 py-1 rounded-full text-[11.5px] font-semibold border transition ${
                    heading === h
                      ? "bg-[#0F2244] text-white border-[#0F2244]"
                      : "bg-white text-[#0F172A] border-slate-200 hover:border-slate-300"
                  }`}
                  data-testid={`cc-vessel-preview-heading-${h}`}
                >
                  {h}°
                </button>
              ))}
            </div>
          </div>
          <div className="h-6 w-px bg-slate-200" />
          <label className="flex items-center gap-2 text-[12.5px] font-semibold text-[#0F172A]">
            <input
              type="checkbox"
              checked={pulse}
              onChange={(e) => setPulse(e.target.checked)}
              data-testid="cc-vessel-preview-pulse-toggle"
            />
            Pulse (future capability)
          </label>
          <div className="ml-auto text-[11px] text-[#94A3B8]">
            Static design only — motion listed for context, not implemented.
          </div>
        </div>
      </div>

      {/* Concepts grid — approved first, then exploration reference */}
      <div className="max-w-6xl mx-auto px-8 py-8 space-y-5">
        {[approved, ...explorations].map((v, idx) => {
          if (!v) return null;
          const isApproved = !!v.approved;
          const isFirstExploration = !isApproved && idx === 1;
          return (
            <React.Fragment key={v.id}>
              {isFirstExploration && (
                <div
                  className="pt-4 pb-1 border-t border-slate-200/70 flex items-baseline gap-3"
                  data-testid="cc-vessel-preview-exploration-divider"
                >
                  <div className="text-[10.5px] font-black tracking-[0.18em] uppercase text-[#94A3B8]">
                    Exploration reference
                  </div>
                  <div className="text-[11px] text-[#94A3B8] italic">
                    kept for one release cycle · retiring after the next milestone
                  </div>
                </div>
              )}
              <section
                className={`bg-white rounded-2xl border overflow-hidden ${
                  isApproved
                    ? "border-[#0F2244] shadow-[0_2px_20px_-8px_rgba(15,34,68,0.35)] ring-1 ring-[#C28E42]/40"
                    : "border-slate-200"
                }`}
                data-testid={`cc-vessel-preview-concept-${v.id}`}
              >
                {/* Card header */}
                <div className={`px-6 py-4 border-b border-slate-100 ${isApproved ? "bg-[#0F2244] text-white" : ""}`}>
                  <div className="flex items-baseline justify-between gap-6">
                    <div>
                      <div className={`text-[10.5px] font-black tracking-[0.18em] uppercase ${isApproved ? "text-[#C28E42]" : "text-[#C28E42]"}`}>
                        {isApproved
                          ? `APPROVED · CC-ICON-001 · Concept ${v.id.toUpperCase()}`
                          : `Concept ${v.id.toUpperCase()}`}
                      </div>
                      <h2 className={`font-display font-bold text-[19px] mt-0.5 ${isApproved ? "text-white" : "text-[#0F172A]"}`}>
                        {v.name}
                      </h2>
                    </div>
                    <div className={`text-[12.5px] max-w-lg leading-relaxed ${isApproved ? "text-white/85" : "text-[#475569]"}`}>
                      {v.rationale}
                    </div>
                  </div>
                </div>

                {/* Sizes on light background */}
                <div className="px-6 py-6">
                  <div className="text-[10.5px] font-black tracking-[0.15em] uppercase text-[#94A3B8] mb-3">
                    Light map surface
                  </div>
                  <div
                    className="rounded-xl px-6 py-8 flex items-end gap-10 flex-wrap"
                    style={{
                      background:
                        "linear-gradient(135deg, #E8EEF4 0%, #F1F5FA 50%, #DDE6EF 100%)",
                    }}
                  >
                    {SIZES.map((s) => (
                      <div key={s} className="flex flex-col items-center gap-2">
                        <VesselMarker variant={v.id} size={s} heading={heading} pulse={pulse} />
                        <div className="text-[10.5px] font-semibold tabular-nums text-[#64748B]">{s}px</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* On dark map background */}
                <div className="px-6 pb-6">
                  <div className="text-[10.5px] font-black tracking-[0.15em] uppercase text-[#94A3B8] mb-3">
                    Dark map surface
                  </div>
                  <div
                    className="rounded-xl px-6 py-8 flex items-end gap-10 flex-wrap"
                    style={{
                      background:
                        "linear-gradient(135deg, #1E3A5F 0%, #0F2244 60%, #0A1830 100%)",
                    }}
                  >
                    {SIZES.map((s) => (
                      <div key={s} className="flex flex-col items-center gap-2">
                        <VesselMarker variant={v.id} size={s} heading={heading} pulse={pulse} />
                        <div className="text-[10.5px] font-semibold tabular-nums text-white/70">{s}px</div>
                      </div>
                    ))}
                  </div>
                </div>
              </section>
            </React.Fragment>
          );
        })}
      </div>

      {/* Footer / governance note */}
      <div className="max-w-6xl mx-auto px-8 pb-16">
        <div className="rounded-xl border border-[#0F2244]/10 bg-white px-6 py-5 text-[12.5px] text-[#475569] leading-relaxed">
          <div className="text-[10.5px] font-black tracking-[0.15em] uppercase text-[#0F2244] mb-2">
            Governance
          </div>
          <strong className="text-[#0F172A]">CC-ICON-001</strong> is ratified and lives in
          the Design System. Governance record:{" "}
          <code className="mx-1 px-1.5 py-0.5 rounded bg-slate-100 text-[#0F2244] font-mono text-[11.5px]">
            /docs/governance/design/CC-ICON-001-vessel-marker.md
          </code>
          . Design tokens are locked at{" "}
          <code className="mx-1 px-1.5 py-0.5 rounded bg-slate-100 text-[#0F2244] font-mono text-[11.5px]">
            --cc-vessel-*
          </code>
          in <code className="font-mono">index.css</code>. CC-ICON-001 is a governed
          navigation-specific variant of the Cruise Command brand mark and is{" "}
          <strong className="text-[#0F172A]">not</strong> to be substituted for the primary
          corporate logo in headers, sign-in surfaces, or marketing collateral.
        </div>
      </div>
    </div>
  );
}
